import { z } from 'zod';
import { EntityIdSchema, IsoDateSchema } from './common';

/**
 * Respuesta individual de un participante en la sincronización desde Forms.
 */
export const SyncFormsRespuestaSchema = z.object({
  cedula: z.string(),
  nombre: z.string().min(1),
  apellido: z.string().min(1),
  email: z.string().email().optional(),
  telefono: z.string().optional(),
});
export type SyncFormsRespuesta = z.infer<typeof SyncFormsRespuestaSchema>;

/**
 * Body de POST /api/v1/sync/forms (webhook HMAC).
 */
export const SyncFormsBodySchema = z.object({
  programa_id: z.string().min(1),
  cohorte_id: EntityIdSchema,
  submitted_after: IsoDateSchema.optional(),
  respuestas: z.array(SyncFormsRespuestaSchema),
});
export type SyncFormsBody = z.infer<typeof SyncFormsBodySchema>;

/**
 * Body de POST /api/v1/sync/forms/manual (disparo desde dashboard).
 *
 * sin uso: la importación se dispara desde la hoja (GAS).
 * La ruta `POST /api/v1/sync/forms/manual` fue retirada: ejecutaba
 * `sincronizarParticipantes({respuestas: []})`, o sea que SIEMPRE devolvía
 * 0/0/0 sin importar nada. El schema se conserva por compatibilidad.
 */
export const SyncFormsManualBodySchema = z.object({
  cohorte_id: EntityIdSchema,
});
export type SyncFormsManualBody = z.infer<typeof SyncFormsManualBodySchema>;

/**
 * Response de POST /api/v1/sync/forms (webhook del GAS).
 *
 * La forma la devuelve el GAS; el panel ya no la consume (muestra el estado
 * con `GET /sync/estado`).
 */
export const SyncFormsResponseSchema = z.object({
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  alumnos_creados: z.number().int().nonnegative().optional(),
  alumnos_reusados: z.number().int().nonnegative().optional(),
  /**
   * Respuestas que NO se importaron, con el motivo. La validación es por
   * campo: una fila inválida no bloquea el resto del lote.
   */
  errores: z.array(z.object({ cedula: z.string(), message: z.string() })).optional(),
  /** @deprecated usar `errores`. */
  errors: z.array(z.object({ cedula: z.string(), message: z.string() })).optional(),
});
export type SyncFormsResponse = z.infer<typeof SyncFormsResponseSchema>;

/**
 * Estado de UNA corrida de sincronización desde Google Forms.
 *
 * Sale de `audit_log` (`accion = 'sync.forms'`): los contadores vienen del
 * `metadata_json` de la fila, proyectados a campos planos.
 */
export const SyncFormsEstadoFilaSchema = z.object({
  /** Momento de la corrida (ISO 8601, UTC). */
  created_at: z.string(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  alumnos_creados: z.number().int().nonnegative(),
  alumnos_reusados: z.number().int().nonnegative(),
  /** Cursor que el GAS mandó en esa corrida (`null` si no lo mandó). */
  submitted_after: z.string().nullable(),
});
export type SyncFormsEstadoFila = z.infer<typeof SyncFormsEstadoFilaSchema>;

/**
 * Response de GET /api/v1/sync/estado?cohorte_id=<id>.
 *
 * `ultima_sync` es `null` cuando la cohorte nunca se sincronizó desde Forms
 * (eso NO es un error: la ruta responde 200 igual).
 */
export const SyncFormsEstadoResponseSchema = z.object({
  data: z.object({
    ultima_sync: SyncFormsEstadoFilaSchema.nullable(),
    syncs: z.array(SyncFormsEstadoFilaSchema),
    /** Total de corridas de esa cohorte (aunque solo se devuelvan las últimas 20). */
    total: z.number().int().nonnegative(),
  }),
});
export type SyncFormsEstadoResponse = z.infer<typeof SyncFormsEstadoResponseSchema>;